import React, { useState } from 'react';
import { toast } from 'sonner';
import { CalendarDays, CheckCircle2, ExternalLink, User as UserIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { aiApi } from '@/utils/aiApi';

export const STATUS_OPTIONS = [
  { value: 'todo', label: 'To Do' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
];
export const statusLabel = (s) => STATUS_OPTIONS.find((o) => o.value === s)?.label || s;

export const formatDue = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

// Move a task between Kanban columns; owners move directly, members create an approval request.
export const useMoveTask = (onMoved) => {
  const [moving, setMoving] = useState(false);
  const move = async (card, status) => {
    if (status === card.status) return;
    setMoving(true);
    try {
      const res = await aiApi.moveTask(card.id, status);
      if (/approval/i.test(res?.message || '')) {
        toast.info(`“${card.title}” — ${res.message}`);
      } else {
        toast.success(`“${card.title}” moved to ${statusLabel(status)}`);
        onMoved?.({ ...card, status, status_label: statusLabel(status) });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not move the task');
    } finally {
      setMoving(false);
    }
  };
  return { move, moving };
};

const TaskCard = ({ card, onOpen, onMoved }) => {
  const { move, moving } = useMoveTask(onMoved);
  return (
    <div className={`chat-task-card status-${card.status}`} data-testid={`chat-task-card-${card.id}`}>
      <button type="button" className="chat-task-card-main" onClick={() => onOpen(card.id)} data-testid={`chat-task-open-${card.id}`}>
        <span className="chat-task-title">{card.title}</span>
        <span className="chat-task-meta">
          <span className={`chat-task-status ${card.status}`} data-testid={`chat-task-status-${card.id}`}>{card.status === 'done' && <CheckCircle2 size={11} />}{statusLabel(card.status)}</span>
          {card.priority && <span className={`chat-task-priority ${card.priority}`}>{card.priority}</span>}
          {card.assignee_name && <span className="chat-task-bit"><UserIcon size={11} /> {card.assignee_name}</span>}
          {card.due_date && <span className="chat-task-bit"><CalendarDays size={11} /> {formatDue(card.due_date)}</span>}
          {card.subtasks_total > 0 && <span className="chat-task-bit">{card.subtasks_done}/{card.subtasks_total} steps</span>}
          <ExternalLink size={11} className="chat-task-open-icon" aria-hidden="true" />
        </span>
      </button>
      <Select value={card.status} onValueChange={(v) => move(card, v)} disabled={moving}>
        <SelectTrigger className="chat-task-move" aria-label={`Move ${card.title}`} data-testid={`chat-task-move-${card.id}`}>
          <SelectValue placeholder="Move to" />
        </SelectTrigger>
        <SelectContent align="end">
          {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`chat-task-move-option-${card.id}-${o.value}`}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
};

// Strip of real tasks mentioned in an answer.
export const TaskCardStrip = ({ cards, onOpen, onMoved }) => {
  if (!cards?.length) return null;
  return (
    <div className="chat-task-strip" data-testid="chat-task-strip">
      <div className="chat-task-strip-label">Tasks mentioned</div>
      <div className="chat-task-strip-list">
        {cards.map((c) => <TaskCard key={c.id} card={c} onOpen={onOpen} onMoved={onMoved} />)}
      </div>
    </div>
  );
};
